import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Screen } from '@/components/ui/Screen';
import { TextField } from '@/components/ui/TextField';
import { AuthActionError, signInWithPassword } from '@/lib/auth/actions';

const brandLogo = require('../../../assets/images/brand-logo.png');
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignInScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setFormError(null);
    if (!EMAIL_PATTERN.test(email.trim())) {
      setFormError(t('auth.errors.invalidEmail'));
      return;
    }
    if (password.length < 8) {
      setFormError(t('auth.errors.shortPassword'));
      return;
    }
    setSubmitting(true);
    try {
      await signInWithPassword(email.trim(), password);
      router.replace('/(tabs)/profile');
    } catch (error) {
      setFormError(
        error instanceof AuthActionError ? t(error.translationKey) : t('auth.errors.generic'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen watermark={false}>
      <BackHeader />
      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 40, gap: 22 }}>
        {/* Brand header */}
        <View className="items-center gap-3 pt-2">
          <Image
            source={brandLogo}
            style={{ height: 104, width: 104, borderRadius: 24 }}
            contentFit="cover"
          />
          <View className="items-center gap-1.5">
            <AppText variant="title" className="text-center">
              {t('auth.welcomeTitle')}
            </AppText>
            <AppText variant="caption" className="max-w-[300px] text-center leading-5">
              {t('auth.welcomeSubtitle')}
            </AppText>
          </View>
        </View>

        <SocialAuthButtons
          onError={setFormError}
          onSuccess={() => router.replace('/(tabs)/profile')}
        />

        <View className="flex-row items-center gap-3">
          <View className="h-px flex-1 bg-line" />
          <AppText variant="micro" className="text-muted">
            {t('auth.orWithEmail')}
          </AppText>
          <View className="h-px flex-1 bg-line" />
        </View>

        <View className="gap-4">
          <TextField
            label={t('auth.email')}
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            autoComplete="email"
            keyboardType="email-address"
            textContentType="emailAddress"
          />
          <TextField
            label={t('auth.password')}
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoComplete="password"
            textContentType="password"
          />
          {formError ? (
            <AppText variant="caption" className="text-loss">
              {formError}
            </AppText>
          ) : null}
          <Button label={t('auth.signIn')} loading={submitting} onPress={submit} />
          <Button
            label={t('auth.needAccount')}
            variant="ghost"
            onPress={() => router.replace('/auth/sign-up')}
          />
        </View>
      </ScrollView>
    </Screen>
  );
}
